import React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ContentCopyOutlined from '@mui/icons-material/ContentCopyOutlined';
import OpenInNewOutlined from '@mui/icons-material/OpenInNewOutlined';
import { Button } from '@/ui';
import { HEADER_HEIGHT } from '@/theme';
import { encodeParam, type TestLink } from '../buildTestLink';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

interface Props {
  link: TestLink | null;
  onCopy: () => void;
  onOpen: () => void;
  /** Where it pins while the form scrolls; defaults to just under the app header. */
  pinTop?: number;
}

const Muted: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component='span' sx={{ color: 'text.secondary' }}>
    {children}
  </Box>
);

/** The link, coloured key/value so a wrong param stands out, kept in view while the form scrolls. */
const LinkOutput: React.FC<Props> = ({ link, onCopy, onOpen, pinTop = HEADER_HEIGHT + 8 }) => (
  // The app header is sticky too: pin just under it, and below its layer so it still wins.
  <Card
    variant='outlined'
    sx={{
      position: 'sticky',
      top: pinTop,
      zIndex: (theme) => theme.zIndex.appBar - 1,
      borderColor: 'primary.main',
      borderWidth: 2,
    }}
  >
    <CardContent>
      {link ? (
        <Typography
          data-testid='test-link-url'
          component='p'
          sx={{ fontFamily: MONO, fontSize: 14, wordBreak: 'break-all', mb: 2 }}
        >
          <Muted>{link.origin}</Muted>
          {link.path}
          <Muted>?</Muted>
          {link.parts.map((part, i) => (
            <React.Fragment key={`${part.key}-${i}`}>
              {i > 0 && <Muted>&amp;</Muted>}
              <Box component='span' sx={{ color: part.kind === 'fixed' ? 'warning.main' : 'secondary.main' }}>
                {encodeParam(part.key)}
              </Box>
              =
              <Box component='span' sx={{ color: part.kind === 'fixed' ? 'warning.main' : 'success.main' }}>
                {encodeParam(part.value)}
              </Box>
            </React.Fragment>
          ))}
        </Typography>
      ) : (
        <Typography data-testid='test-link-url' color='text.secondary' sx={{ mb: 2 }}>
          Choose a domain to build a link.
        </Typography>
      )}
      <Stack direction='row' spacing={1}>
        <Button color='primary' startIcon={<ContentCopyOutlined />} disabled={!link} onClick={onCopy}>
          Copy link
        </Button>
        <Button color='primary' variant='outlined' startIcon={<OpenInNewOutlined />} disabled={!link} onClick={onOpen}>
          Open in new tab
        </Button>
      </Stack>
    </CardContent>
  </Card>
);

export default LinkOutput;
